import styled from "styled-components";
import { darken, getContrast, lighten, parseToRgb, readableColor } from "polished";
import { useTheme } from "../../contexts/ThemeContext";

const validColor = (value, fallback) => {
  try {
    const color = parseToRgb(value);
    return color.alpha !== undefined && color.alpha < 1 ? fallback : value;
  } catch {
    return fallback;
  }
};

const PageButton = styled.button`
  min-width: 36px;
  height: 36px;
  border: 1px solid ${({ $border }) => $border};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, color 0.2s;
  background-color: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};

  &:hover:not(:disabled) {
    background-color: ${({ $hover }) => $hover};
    color: ${({ $hover }) => readableColor($hover)};
  }

  &:focus-visible {
    outline: 2px solid ${({ $focus }) => $focus};
    outline-offset: 3px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const theme = useTheme();
  const surface = validColor(
    theme.colors[`background_${theme.mode}`],
    theme.mode === "dark" ? "#000" : "#fff"
  );
  const surfaceText = readableColor(surface);
  const configuredAccent = validColor(theme.colors[`button_${theme.mode}`], surfaceText);
  const accent = getContrast(configuredAccent, surface) >= 1.5 ? configuredAccent : surfaceText;
  const configuredAccentText = validColor(theme.colors[`text_button_${theme.mode}`], readableColor(accent));
  const accentText = getContrast(configuredAccentText, accent) >= 4.5
    ? configuredAccentText
    : readableColor(accent);
  const hoverFor = (color) => readableColor(color) === "#fff" ? lighten(0.1, color) : darken(0.1, color);
  const normalStyle = {
    $background: surface,
    $color: surfaceText,
    $border: surfaceText,
    $hover: hoverFor(surface),
    $focus: surfaceText,
  };
  const activeStyle = {
    $background: accent,
    $color: accentText,
    $border: getContrast(accent, surface) >= 3 ? accent : surfaceText,
    $hover: hoverFor(accent),
    $focus: surfaceText,
    $active: true,
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };
  const pages = getPageNumbers();

  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "24px 0 42px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: 6, borderRadius: 10, backgroundColor: surface }}>
      <PageButton {...normalStyle} onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} aria-label="Página anterior">‹</PageButton>
      {pages[0] > 1 && (
        <>
          <PageButton {...normalStyle} onClick={() => onPageChange(1)}>1</PageButton>
          {pages[0] > 2 && <span style={{ padding: "0 4px", color: surfaceText }}>…</span>}
        </>
      )}
      {pages.map((page) => (
        <PageButton key={page} {...(page === currentPage ? activeStyle : normalStyle)} onClick={() => onPageChange(page)} aria-current={page === currentPage ? "page" : undefined}>
          {page}
        </PageButton>
      ))}
      {pages[pages.length - 1] < totalPages && (
        <>
          {pages[pages.length - 1] < totalPages - 1 && <span style={{ padding: "0 4px", color: surfaceText }}>…</span>}
          <PageButton {...normalStyle} onClick={() => onPageChange(totalPages)}>{totalPages}</PageButton>
        </>
      )}
      <PageButton {...normalStyle} onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Página siguiente">›</PageButton>
      </div>
    </div>
  );
};

export default Pagination;
